import { useState } from "react";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
} from "@tanstack/react-table";

type UseDataTableOptions<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  getRowId: (row: T) => string;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean;
} & Pick<TableOptions<T>, "meta">;

export function useDataTable<T>({
  data,
  columns,
  getRowId,
  sorting,
  onSortingChange,
  manualSorting = false,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  meta,
}: UseDataTableOptions<T>) {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  return useReactTable({
    data,
    columns,
    getRowId,
    state: {
      sorting,
      columnSizing,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    onSortingChange,
    onColumnSizingChange: setColumnSizing,
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    enableRowSelection,
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    enableSortingRemoval: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 72,
      maxSize: 480,
    },
    meta,
  });
}
